import { Box } from "@material-ui/core";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import { Add } from "@material-ui/icons";
import ClearIcon from "@material-ui/icons/Clear";
import { useMutation, useNotify } from "ra-core";
import React, { useEffect, useState } from "react";
import { SimpleForm } from "react-admin";
import { Booking } from "../../types";

// Maximum allowed transaction numbers
const MAX_TRANSACTION_NUMBERS = 10;

interface Props {
  data: Booking;
}

const OutboundTransactionNumbersDialog: React.FC<Props> = ({ data }) => {
  const [open, setOpen] = useState(false);
  const notify = useNotify();
  const [transactionDatas, setTransactionDatas] = useState<
    { id: number; value: string }[]
  >([{ id: 1, value: "" }]); // Default one form
  const [nextId, setNextId] = useState(2); // Start ID from 2
  const [isLoading, setIsLoading] = useState(false);

  // Load existing outbound transaction numbers
  useEffect(() => {
    if (open && data.outbound_transaction_numbers) {
      setIsLoading(true);

      try {
        let existingNumbers: string[] = [];

        // Parse the outbound_transaction_numbers based on its type
        if (Array.isArray(data.outbound_transaction_numbers)) {
          existingNumbers = data.outbound_transaction_numbers;
        } else if (typeof data.outbound_transaction_numbers === "string") {
          const transactionNumberStr =
            data.outbound_transaction_numbers as string;
          if (transactionNumberStr.includes("[")) {
            existingNumbers = JSON.parse(transactionNumberStr);
          } else {
            existingNumbers = [transactionNumberStr];
          }
        }

        // If we have existing numbers, populate the form with them
        // But limit to MAX_TRANSACTION_NUMBERS
        if (existingNumbers.length > 0) {
          const limitedNumbers = existingNumbers.slice(
            0,
            MAX_TRANSACTION_NUMBERS
          );
          const formattedData = limitedNumbers.map((value, index) => ({
            id: index + 1,
            value,
          }));

          setTransactionDatas(formattedData);
          setNextId(
            Math.min(existingNumbers.length + 1, MAX_TRANSACTION_NUMBERS + 1)
          );
        }
      } catch (error) {
        console.error("Error parsing existing transaction numbers:", error);
        notify("Error loading existing transaction numbers", "warning");
      }

      setIsLoading(false);
    }
  }, [open, data.outbound_transaction_numbers, notify]);

  const handleTransactionChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    id: number
  ) => {
    const newTransactionDatas = transactionDatas.map((transactionData) =>
      transactionData.id === id
        ? { ...transactionData, value: event.target.value }
        : transactionData
    );
    setTransactionDatas(newTransactionDatas);
  };

  const [submitNumbers] = useMutation(
    {
      type: "update",
      resource: "bookings",
      payload: {
        id: data.id,
        data: {
          action: "submit_outbound_transaction_number",
          outbound_transaction_numbers: transactionDatas
            .map((td) => td.value)
            .filter((value) => value !== ""),
        },
      },
    },
    {
      onSuccess: () => {
        notify("Outbound transaction numbers submitted successfully", "info");
        setOpen(false);
      },
      onFailure: (error) => notify(`Error: ${error.message}`, "warning"),
    }
  );

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    // Reset form state when closing dialog without submitting
    setTransactionDatas([{ id: 1, value: "" }]);
    setNextId(2);
  };

  const handleSubmit = () => {
    const validNumbers = transactionDatas
      .map((td) => td.value)
      .filter((value) => value !== "");

    if (validNumbers.length > 0) {
      submitNumbers();
    } else {
      notify("Please add at least one transaction number", "warning");
    }
  };

  // Check if we've reached the maximum number of transaction fields
  const isMaxReached = transactionDatas.length >= MAX_TRANSACTION_NUMBERS;

  return (
    <>
      <Button
        size="small"
        variant="contained"
        color="primary"
        onClick={handleOpen}
      >
        Outbound
      </Button>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>Submit Outbound Transaction Numbers</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Enter outbound transaction numbers for booking ID: {data.id}
            {isMaxReached && (
              <span
                style={{ color: "red", display: "block", marginTop: "8px" }}
              >
                Maximum {MAX_TRANSACTION_NUMBERS} transaction numbers allowed
              </span>
            )}
          </DialogContentText>

          {isLoading ? (
            <DialogContentText>
              Loading existing transaction numbers...
            </DialogContentText>
          ) : (
            <SimpleForm toolbar={<></>}>
              {transactionDatas.map((transactionData, index) => (
                <Box
                  key={transactionData.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: "10px",
                  }}
                >
                  {/* Using a regular input instead of TextInput component */}
                  <input
                    type="text"
                    placeholder={`Transaction Number ${index + 1}`}
                    value={transactionData.value}
                    onChange={(event) =>
                      handleTransactionChange(event, transactionData.id)
                    }
                    style={{
                      flexGrow: 1,
                      padding: "10px",
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                    }}
                  />
                  {/* delete input button */}
                  {transactionDatas.length > 1 && (
                    <ClearIcon
                      style={{
                        color: "red",
                        cursor: "pointer",
                        marginLeft: "10px",
                      }}
                      onClick={() => {
                        // Ensure there's at least one form
                        if (transactionDatas.length > 1) {
                          const newTransactionDatas = transactionDatas.filter(
                            (td) => td.id !== transactionData.id
                          );
                          setTransactionDatas(newTransactionDatas);
                        }
                      }}
                    />
                  )}
                </Box>
              ))}
              {/* add input button - only show if under max limit */}
              {!isMaxReached && (
                <Button
                  startIcon={<Add />}
                  style={{
                    backgroundColor: "darkblue",
                    color: "white",
                    marginTop: 10,
                  }}
                  onClick={() => {
                    setTransactionDatas([
                      ...transactionDatas,
                      { id: nextId, value: "" },
                    ]);
                    setNextId((prevId) => prevId + 1);
                  }}
                >
                  Add
                </Button>
              )}
            </SimpleForm>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            variant="contained"
            disabled={isLoading}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OutboundTransactionNumbersDialog;
